import type { ReactNode } from "react";
import { clamp } from "./format";
import type { MetricSnapshot, ServiceStatus } from "./types";

type Tone = "good" | "warn" | "critical" | "cyan" | "muted";

interface GaugeProps {
  label: string;
  value: number | null | undefined;
  unit?: string;
  tone?: Tone;
  footer?: ReactNode;
}

export function RingGauge({ label, value, unit = "%", tone = "cyan", footer }: GaugeProps) {
  const normalized = value == null ? 0 : clamp(value);
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (normalized / 100) * circumference;

  return (
    <div className="ring-gauge" data-tone={tone}>
      <svg viewBox="0 0 116 116" aria-hidden="true">
        <circle className="ring-track" cx="58" cy="58" r={radius} />
        <circle
          className="ring-value"
          cx="58"
          cy="58"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="ring-copy">
        <span className="ring-value-label">{value == null ? "--" : Math.round(value)}</span>
        <span className="ring-unit">{value == null ? "" : unit}</span>
        <span className="ring-label">{label}</span>
      </div>
      {footer ? <div className="ring-footer">{footer}</div> : null}
    </div>
  );
}

interface SparklineProps {
  values: number[];
  tone?: Tone;
  height?: number;
  max?: number;
}

export function Sparkline({ values, tone = "cyan", height = 72, max }: SparklineProps) {
  const viewWidth = 260;
  const viewHeight = height;
  const safeValues = values.length > 1 ? values : [0, values[0] || 0];
  const maximum = max ?? Math.max(...safeValues, 1);
  const minimum = Math.min(...safeValues, 0);
  const range = Math.max(maximum - minimum, 1);
  const points = safeValues.map((value, index) => {
    const x = (index / Math.max(safeValues.length - 1, 1)) * viewWidth;
    const y = viewHeight - ((value - minimum) / range) * (viewHeight - 10) - 5;
    return [x, y] as const;
  });
  const path = points
    .map(([x, y], index) => `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(" ");
  const area = `${path} L ${viewWidth} ${viewHeight} L 0 ${viewHeight} Z`;

  return (
    <svg className="sparkline" data-tone={tone} viewBox={`0 0 ${viewWidth} ${viewHeight}`} preserveAspectRatio="none">
      <path className="spark-area" d={area} />
      <path className="spark-path" d={path} />
    </svg>
  );
}

interface BarProps {
  label: string;
  value: number;
  tone?: Tone;
  detail?: string;
}

export function HorizontalMeter({ label, value, tone = "good", detail }: BarProps) {
  return (
    <div className="meter-row" data-tone={tone}>
      <div className="meter-row-header">
        <span>{label}</span>
        <span>{detail || `${Math.round(value)}%`}</span>
      </div>
      <div className="meter-track">
        <span style={{ width: `${clamp(value)}%` }} />
      </div>
    </div>
  );
}

function nodeColor(status: ServiceStatus["status"]) {
  if (status === "down") return "critical";
  if (status === "warn") return "warn";
  return "good";
}

export function TopologyMap({
  snapshot,
  selected,
  onSelect
}: {
  snapshot: MetricSnapshot | null;
  selected: string | null;
  onSelect: (name: string) => void;
}) {
  const services = snapshot?.services ?? [];
  const nodes = [
    { name: "CPU", x: 38, y: 42, tone: snapshot?.cpu.usage && snapshot.cpu.usage > 85 ? "warn" : "good" },
    { name: "GPU", x: 64, y: 28, tone: snapshot?.gpu.available ? "cyan" : "muted" },
    { name: "NET", x: 69, y: 68, tone: "cyan" },
    ...services.slice(0, 5).map((service, index) => ({
      name: service.name,
      x: [20, 25, 47, 78, 84][index],
      y: [68, 23, 78, 46, 18][index],
      tone: nodeColor(service.status)
    }))
  ];
  const hub = { x: 50, y: 52 };

  return (
    <div className="topology">
      <svg viewBox="0 0 100 90" role="img" aria-label="Machine topology">
        <defs>
          <filter id="nodeGlow">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {nodes.map((node) => (
          <line
            key={`${node.name}-edge`}
            className="topology-edge"
            x1={hub.x}
            y1={hub.y}
            x2={node.x}
            y2={node.y}
          />
        ))}
        <circle className="topology-hub" cx={hub.x} cy={hub.y} r="6" />
        {nodes.map((node) => (
          <g
            key={node.name}
            className={selected === node.name ? "topology-node is-selected" : "topology-node"}
            data-tone={node.tone}
            onClick={() => onSelect(node.name)}
            tabIndex={0}
            role="button"
            aria-label={`Select ${node.name}`}
          >
            <circle cx={node.x} cy={node.y} r="5.2" filter="url(#nodeGlow)" />
            <text x={node.x} y={node.y + 11}>
              {node.name.length > 12 ? node.name.slice(0, 12) : node.name}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
